import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@lbmx/phoenix-lib-core';
import {
  SpinnerService,
  ToastrNotificationService
} from '@lbmx/phoenix-lib-utils';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { IConfigCountry, IConfigProvince, IDropDown, ISupplierProfileForm } from 'src/app/dtos/onboarding/onboarding.dto';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';

@Component({
  selector: 'supplieronboarding',
  templateUrl: './supplier.component.html',
  styleUrls: ['./supplier.component.scss']
})

export class SupplierComponent implements OnInit, OnDestroy {

  public subscriptions: Subscription[] = [];

  public Types: any = [{ label: 'PDF', value: 'PDF' }, { label: 'Webform', value: 'Webform' }, { label: 'X12/EDIFACT EDI', value: 'X12/EDIFACT EDI' }, { label: 'Custom EDI', value: 'Custom EDI', }];
  public countriesDropDown: IDropDown[];
  public provincesDropDown: IDropDown[];
  public provinces: IConfigProvince[];

  public supplierProfileForm: FormGroup = this.formBuilder.group(
    {
      companyName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      city: ['', [Validators.required]],
      country: ['', [Validators.required]],
      province: ['', [Validators.required]],
      postalCode: ['', [Validators.required]],
      mainPhone: ['', [Validators.required]],
      groupSupplierNumber: ['', [Validators.required]],
      generalName: ['', [Validators.required]],
      generalPhone: ['', [Validators.required]],
      generalEmail: ['', [Validators.required, Validators.email]],
      accountingName: ['', [Validators.required]],
      accountingPhone: ['', [Validators.required]],
      accountingEmail: ['', [Validators.required, Validators.email]],
      ediName: ['', [Validators.required]],
      ediPhone: ['', [Validators.required]],
      ediEmail: ['', [Validators.required, Validators.email]],
      connectionType: [''],
    },
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private loggerService: LoggerService,
    private toastSrv: ToastrNotificationService,
    private spinnerService: SpinnerService,
    private onboardingService: OnboardingService,
    private translocoService: TranslocoService,
  ) { }

  public ngOnInit(): void {
    this.getCountries();
    this.getProvinces();
  }

  public getCountries() {
    this.onboardingService.getCountries()
      .pipe()
      .subscribe((val) => {
        this.countriesDropDown = val.map(
          (country: IConfigCountry): IDropDown => {
            return {
              label: country.countryName,
              value: country.countryCode.trim(),
            };
          }
        );
      });
  }

  public getProvinces() {
    this.onboardingService.getProvinces()
      .pipe()
      .subscribe((val) => {
        this.provinces = val;
      });
  }

  public onCountryDropDownSelect() {
    const filteredProv = this.provinces.filter(prov => prov.countryCode === this.supplierProfileForm.get('country').value);
    this.provincesDropDown = filteredProv.map((province: IConfigProvince): IDropDown => {
      return {
        label: province.provinceName,
        value: province.provinceCode.trim(),
      };
    });
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    this.spinnerService.off();
  }

  public buildAddRequest(): ISupplierProfileForm {
    return {
      companyName: this.supplierProfileForm.value.companyName,
      address: this.supplierProfileForm.value.address,
      city: this.supplierProfileForm.value.city,
      country: this.supplierProfileForm.value.country,
      province: this.supplierProfileForm.value.province,
      postalCode: this.supplierProfileForm.value.postalCode,
      mainPhone: this.supplierProfileForm.value.mainPhone,
      groupSupplierNumber: this.supplierProfileForm.value.groupSupplierNumber,
      generalName: this.supplierProfileForm.value.groupSupplierNumber,
      generalPhone: this.supplierProfileForm.value.groupSupplierNumber,
      generalEmail: this.supplierProfileForm.value.groupSupplierNumber,
      accountingName: this.supplierProfileForm.value.groupSupplierNumber,
      accountingPhone: this.supplierProfileForm.value.groupSupplierNumber,
      accountingEmail: this.supplierProfileForm.value.groupSupplierNumber,
      ediName: this.supplierProfileForm.value.groupSupplierNumber,
      ediPhone: this.supplierProfileForm.value.groupSupplierNumber,
      ediEmail: this.supplierProfileForm.value.groupSupplierNumber,
      connectionType: this.supplierProfileForm.value.connectionType,
    };
  }

  public save() {
    this.addSupplier();
  }

  public addSupplier() {
    this.spinnerService.on();
    this.subscriptions.push(
      this.onboardingService
        .submitSupplier(this.buildAddRequest())
        .pipe(finalize(() => this.spinnerService.off()))
        .subscribe(
          (response) => {
            this.loggerService.info(
              'AddSupplierResponse',
              'AddSupplierService.addSupplier',
              JSON.stringify(response)
            );
            if (response.wasAdded === true) {
              this.toastSrv.successNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_SUPPLIER_SUCCESS'
                ),
                null,
                5000
              );
              this.router.navigate(['..'], { relativeTo: this.route });
            }
          },
          (error) => {
            if (error.error != null && error.error.errorCode === 3) {
              this.toastSrv.warnNotify(
                this.translocoService.translate(
                  'ONBOARDING.SUPPLIER_ALREADY_EXIST'
                ),
                null,
                5000
              );
            } else {
              this.toastSrv.errorNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_SUPPLIER_FAILURE'
                ),
                null,
                5000
              );
            }
            this.loggerService.error(
              'AddSupplierResponse',
              'AddSupplierService.addSupplier',
              JSON.stringify(error)
            );
          }
        )
    );
  }
}
